import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { HealthBar } from '../components/HealthBar';
import EnemyIntent from '../components/EnemyIntent';
import GameCard from '../components/GameCard';
import { CARDS } from '../game/cards';
import { computeIntentPreview } from '../game/engine';
import { projectionLabel } from '../game/projection';
import { sfx, startHeartbeat, stopHeartbeat } from '../game/sound';
import { RotateCw, Layers, Trash2, Eye, Zap, Pause, ScrollText, Flame, X } from 'lucide-react';
import BrainrotTicker from '../components/BrainrotTicker';
import { AvatarChip } from '../components/Avatar';
import ComboHUD from '../components/ComboHUD';
import PlayerFighter from '../components/PlayerFighter';
import EnemyFighter from '../components/EnemyFighter';
import EnemyIntroCard from '../components/EnemyIntroCard';

const BUFF_TOOLTIPS = {
  STR: 'STRENGTH: adds damage per hit.',
  WEAK: 'WEAK: -25% attack damage.',
  VULN: 'VULNERABLE: takes +50% damage.',
  REFLECT: 'REFLECT: throws damage back to attacker.',
  DRAW: 'Draw an extra card each turn.',
  'STR/T': 'Gain strength every turn.',
  BLK: 'Extra block at start of turn.',
  DODGE: 'Next attack is nullified.',
};

// v1.2: SLOP METER combo tiers
const COMBO_TIERS = [
  { min: 3,  label: 'SLOP!',        color: '#00e5ff' },
  { min: 5,  label: 'MEGA SLOP!',   color: '#a0ff2e' },
  { min: 7,  label: 'ULTRA SLOP!',  color: '#ffe74a' },
  { min: 10, label: 'GIGA SLOP!',   color: '#ff8fd1' },
  { min: 14, label: 'OMEGA SLOP!!', color: '#ff2ea6' },
];
function tierFor(count) {
  let t = null;
  for (const T of COMBO_TIERS) if (count >= T.min) t = T;
  return t;
}

const EMOTES = ['SLOP', 'COPE', 'MOG', 'RATIO', 'GOATED', 'COOKED', 'SUS', 'SEETHE', 'LORE'];
const EMOTE_KIND = { SLOP:'slop', COPE:'cope', MOG:'mog', RATIO:'ratio', GOATED:'goat', COOKED:'cook', SUS:'sus', SEETHE:'seethe', LORE:'lore' };

export default function Combat() {
  const { combat, doPlayCard, doEndTurn, triggerBurst, undoLastCard, canUndo, profile, playerName } = useGame();
  const [enemyShake, setEnemyShake] = useState(false);
  const [enemyShakeHard, setEnemyShakeHard] = useState(false);
  // v3.8 screen shake tier — 'nuke' shakes the whole combat root for massive hits
  const [screenShakeTier, setScreenShakeTier] = useState(null);
  // v3.9 BOSS FINAL BLOW: fullscreen KO overlay when boss/elite drops to 0 HP
  const [finalBlow, setFinalBlow] = useState(null); // { name, sprite, tier } | null
  const [playerShake, setPlayerShake] = useState(false);
  // v3.8 enemy fighter state
  const [enemyAction, setEnemyAction] = useState('idle');
  const [enemyTrigger, setEnemyTrigger] = useState(0);
  const [popups, setPopups] = useState([]);
  const [comboCount, setComboCount] = useState(0);
  const [comboShout, setComboShout] = useState(null);
  const [emote, setEmote] = useState(null);
  const [enemyEmote, setEnemyEmote] = useState(null);
  const [bossIntro, setBossIntro] = useState(false);
  // v3.9 ENEMY INTRO: unified elite + boss cinematic
  const [enemyIntro, setEnemyIntro] = useState(null); // { tier: 'boss'|'elite', name, sprite, affix }
  // v1.7 POLISH: pile peek modal + pause + log
  const [pilePeek, setPilePeek] = useState(null); // 'draw' | 'discard' | 'exhaust' | null
  const [paused, setPaused] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false); // v2.3 QoL #8
  const [showLog, setShowLog] = useState(false);
  const [zoomCard, setZoomCard] = useState(null);
  // v3.7 COMBAT AVATAR: fighter action state — parent-driven animation
  const [fighterAction, setFighterAction] = useState('idle');
  const [fighterTrigger, setFighterTrigger] = useState(0);
  const [hitSpark, setHitSpark] = useState(null);
  const [blockSpark, setBlockSpark] = useState(null);
  const prevEnemyHpRef = useRef(null);
  const prevPlayerHpRef = useRef(null);
  const prevPhaseRef = useRef(0);
  const prevHandLenRef = useRef(null);
  const prevTurnRef = useRef(null);

  useEffect(() => {
    if (!combat) return;
    const s = combat.state;
    if (prevEnemyHpRef.current !== null && s.enemy.hp < prevEnemyHpRef.current) {
      const diff = prevEnemyHpRef.current - s.enemy.hp;
      // v3.9 BOSS FINAL BLOW: fullscreen KO cinematic when a boss/elite reaches 0
      const isFinalBlow = (s.enemy.hp === 0 && prevEnemyHpRef.current > 0 && (s.enemy.isBoss || s.enemy.isElite));
      if (isFinalBlow) {
        const tier = s.enemy.isBoss ? 'boss' : 'elite';
        setFinalBlow({ name: s.enemy.name, sprite: s.enemy.sprite, tier });
        setTimeout(() => setFinalBlow(null), 2100);
      }
      // v3.8 tiered shake: soft <15, hard 15-29, nuke >=30 (screen-wide). FINAL BLOW always nukes.
      const tier = isFinalBlow ? 'nuke' : (diff >= 30 ? 'nuke' : diff >= 15 ? 'hard' : 'soft');
      if (tier === 'nuke') {
        setEnemyShakeHard(true);
        setScreenShakeTier('nuke');
        setTimeout(() => setEnemyShakeHard(false), 420);
        setTimeout(() => setScreenShakeTier(null), 480);
      } else if (tier === 'hard') {
        setEnemyShakeHard(true);
        setScreenShakeTier('hard');
        setTimeout(() => setEnemyShakeHard(false), 380);
        setTimeout(() => setScreenShakeTier(null), 380);
      } else {
        setEnemyShake(true);
        setTimeout(() => setEnemyShake(false), 220);
      }
      // v3.8 enemy fighter — hurt recoil
      setEnemyAction('hurt');
      setEnemyTrigger(t => t + 1);
      setTimeout(() => setEnemyAction(cur => (cur === 'hurt' ? 'idle' : cur)), 320);
      const id = Math.random();
      setPopups(p => [...p, { id, target: 'enemy', text: `-${diff}`, kind: tier === 'soft' ? 'dmg' : 'crit' }]);
      // v3.7 hit spark on enemy sprite when damage lands
      const sparkId = Math.random();
      setHitSpark(sparkId);
      setTimeout(() => setHitSpark(cur => (cur === sparkId ? null : cur)), 450);
      setTimeout(() => setPopups(p => p.filter(x => x.id !== id)), 900);
    }
    if (prevPlayerHpRef.current !== null && s.player.hp < prevPlayerHpRef.current) {
      setPlayerShake(true);
      const diff = prevPlayerHpRef.current - s.player.hp;
      // v3.8 tiered screen shake on player hit too
      const tier = diff >= 25 ? 'nuke' : diff >= 12 ? 'hard' : 'soft';
      if (tier !== 'soft') {
        setScreenShakeTier(tier);
        setTimeout(() => setScreenShakeTier(null), tier === 'nuke' ? 480 : 380);
      }
      const id = Math.random();
      setPopups(p => [...p, { id, target: 'player', text: `-${diff}`, kind: 'dmg' }]);
      // v3.7 trigger fighter 'hurt' recoil
      setFighterAction('hurt');
      setFighterTrigger(t => t + 1);
      setTimeout(() => setFighterAction(cur => (cur === 'hurt' ? 'idle' : cur)), 340);
      // v3.8 enemy fighter — commit the attack lunge at the moment damage lands.
      // v3.9 TIMING FIX: if we're currently in 'windup', delay the attack switch by the windup
      // duration (~260ms) so the wind-back visibly plays before the lunge starts.
      setEnemyAction(cur => {
        if (cur === 'windup') {
          setTimeout(() => {
            setEnemyAction('attack');
            setEnemyTrigger(t => t + 1);
            setTimeout(() => setEnemyAction(c2 => (c2 === 'attack' ? 'idle' : c2)), 520);
          }, 260);
          return 'windup';
        }
        setEnemyTrigger(t => t + 1);
        setTimeout(() => setEnemyAction(c2 => (c2 === 'attack' ? 'idle' : c2)), 520);
        return 'attack';
      });
      setTimeout(() => setPlayerShake(false), 220);
      setTimeout(() => setPopups(p => p.filter(x => x.id !== id)), 900);
      // v1.3: enemy taunt on big hit
      if (diff >= 8) {
        const taunts = ['RATIOED', 'GG NOOB', 'MOG', 'COPE', 'GIT GUD', 'BRAINROT', 'L + RATIO'];
        const line = taunts[Math.floor(Math.random() * taunts.length)];
        const eid = Math.random();
        setEnemyEmote({ id: eid, text: line });
        setTimeout(() => setEnemyEmote(e => (e && e.id === eid) ? null : e), 1100);
      }
    }
    if (prevPlayerHpRef.current !== null && s.player.hp > prevPlayerHpRef.current) {
      const id = Math.random();
      setPopups(p => [...p, { id, target: 'player', text: `+${s.player.hp - prevPlayerHpRef.current}`, kind: 'heal' }]);
      setTimeout(() => setPopups(p => p.filter(x => x.id !== id)), 900);
    }
    // Phase transition
    if (prevPhaseRef.current !== (s.phase || 0)) {
      setEnemyShakeHard(true);
      setTimeout(() => setEnemyShakeHard(false), 380);
      prevPhaseRef.current = s.phase || 0;
      const id = Math.random();
      setPopups(p => [...p, { id, target: 'enemy', text: `PHASE ${(s.phase || 0) + 1}!`, kind: 'phase' }]);
      setTimeout(() => setPopups(p => p.filter(x => x.id !== id)), 1400);
    }
    prevEnemyHpRef.current = s.enemy.hp;
    prevPlayerHpRef.current = s.player.hp;
  }, [combat && combat.state && combat.state.enemy.hp, combat && combat.state && combat.state.player.hp, combat && combat.state && combat.state.phase]);

  // v3.8 ENEMY FIGHTER: intent-based windup/brace/cast when turn flips to enemy.
  // NOTE: engine.js resolves the entire enemy turn synchronously in a single React commit,
  // so we CANNOT watch for `turnOwner === 'enemy'` here. Instead, we pre-fire the animation
  // from `playEndTurn` (the wrapper used by every end-turn call site) BEFORE endTurn resolves.
  const triggerEnemyIntentAnim = () => {
    if (!combat || combat.state.ended) return;
    const intent = combat.state.enemy?.intents?.[0];
    const t = intent?.type;
    let act = null;
    if (t === 'attack') act = 'windup';
    else if (t === 'block' || t === 'attack_block') act = 'brace';
    else if (t === 'debuff' || t === 'buff' || t === 'summon' || t === 'unknown') act = 'cast';
    if (act) {
      setEnemyAction(act);
      setEnemyTrigger(x => x + 1);
      setTimeout(() => setEnemyAction(cur => (cur === act ? 'idle' : cur)), 700);
    }
  };
  // Wrapper that plays the pre-fire animation then delegates to engine's endTurn.
  const playEndTurn = () => {
    triggerEnemyIntentAnim();
    doEndTurn();
  };

  // v2.8 QoL #7: auto-end-turn when enabled + hand can't do anything
  useEffect(() => {
    if (!combat || combat.state.ended) return;
    if (combat.state.turnOwner !== 'player') return;
    if (paused) return;
    let enabled = false;
    try { enabled = localStorage.getItem('friendslop.autoEnd') === '1'; } catch (e) {}
    if (!enabled) return;
    const s = combat.state;
    const nothingPlayable = s.hand.every(cid => {
      const c = CARDS[cid];
      if (!c) return true;
      if (c.unplayable) return true;
      const cost = typeof c.cost === 'number' ? c.cost : 0;
      if (cost > s.player.energy) return true;
      if (c.requiresBlock && s.player.block <= 0) return true;
      return false;
    });
    // Only auto-end if no playable AND no free cards left (energy=0 with nothing free)
    if (nothingPlayable && s.hand.length > 0) {
      const t = setTimeout(() => { playEndTurn(); }, 700);
      return () => clearTimeout(t);
    }
  }, [combat, doEndTurn, paused]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (!combat || combat.state.ended) return;
      if (e.target && ['INPUT','TEXTAREA'].includes(e.target.tagName)) return;
      // v1.7: universal shortcuts
      if (e.key === 'Escape') { e.preventDefault(); setPaused(p => !p); return; }
      if (paused) return; // Ignore other keys while paused
      if (e.key === 'l' || e.key === 'L') { e.preventDefault(); setShowLog(v => !v); return; }
      if (e.key === 'u' || e.key === 'U') { e.preventDefault(); if (undoLastCard()) sfx.click(); return; }
      if (e.key === '[' || e.key === '{') { e.preventDefault(); setPilePeek('draw'); return; }
      if (e.key === ']' || e.key === '}') { e.preventDefault(); setPilePeek('discard'); return; }
      if (e.key >= '1' && e.key <= '9') {
        const idx = parseInt(e.key, 10) - 1;
        if (combat.state.hand[idx] !== undefined) {
          try {
            const c2 = CARDS[combat.state.hand[idx]];
            const t = String(c2?.type || '').toLowerCase();
            if (t === 'attack') sfx.attackCard();
            else if (t === 'power') sfx.powerCard();
            else sfx.skillCard();
            triggerFighterFromCard(c2);
          } catch (e2) {}
          doPlayCard(idx);
        }
      } else if (e.key === 'e' || e.key === 'E' || e.key === ' ') {
        e.preventDefault();
        playEndTurn();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [combat, doPlayCard, doEndTurn, paused, undoLastCard]);

  // v1.2: SLOP METER combo tracking — hand shrinks by 1 = one card played
  useEffect(() => {
    if (!combat) return;
    const s = combat.state;
    if (prevTurnRef.current !== null && s.turn !== prevTurnRef.current) {
      // New turn: reset combo
      setComboCount(0);
      setComboShout(null);
      prevHandLenRef.current = s.hand.length;
      prevTurnRef.current = s.turn;
      return;
    }
    prevTurnRef.current = s.turn;
    if (prevHandLenRef.current !== null && s.hand.length < prevHandLenRef.current) {
      // A card was played
      setComboCount((n) => {
        const next = n + 1;
        const T = tierFor(next);
        if (T && (!tierFor(n) || tierFor(n).min !== T.min)) {
          // Tier crossed — shout + escalating sfx + burst
          const tierIdx = COMBO_TIERS.findIndex(x => x.min === T.min);
          try { sfx.comboHit(tierIdx + 1); } catch (e) {}
          if (tierIdx >= 3) { try { triggerBurst?.('rainbow'); } catch (e) {} }
          setComboShout({ id: Math.random(), text: T.label, color: T.color });
          setTimeout(() => setComboShout(null), 1100);
        }
        return next;
      });
    }
    prevHandLenRef.current = s.hand.length;
  }, [combat, triggerBurst]);

  // v1.2: Boss entrance sting (once per boss encounter)
  const lastBossKeyRef = useRef(null);
  useEffect(() => {
    if (!combat) { lastBossKeyRef.current = null; setEnemyIntro(null); return; }
    const key = `${combat.floorIdx}:${combat.kind}`;
    const isBoss = combat.state.enemy?.isBoss || combat.kind === 'boss' || combat.kind === 'midboss';
    const isElite = combat.state.enemy?.isElite || combat.kind === 'elite';
    if ((isBoss || isElite) && lastBossKeyRef.current !== key) {
      lastBossKeyRef.current = key;
      try { sfx.bossSting(); } catch (e) {}
      const tier = isBoss ? 'boss' : 'elite';
      setEnemyIntro({
        tier,
        name: combat.state.enemy.name,
        sprite: combat.state.enemy.sprite,
        affix: combat.state.enemy.affix || null,
      });
      setBossIntro(isBoss); // keep legacy flag for anything depending on it
      const dur = isBoss ? 2200 : 1600;
      setTimeout(() => { setEnemyIntro(null); setBossIntro(false); }, dur);
    }
  }, [combat?.floorIdx, combat?.kind, combat]);

  // v1.2: Low-HP heartbeat + vignette
  useEffect(() => {
    if (!combat) { stopHeartbeat(); document.body.classList.remove('low-hp'); return; }
    const s = combat.state;
    const ratio = s.player.hp / Math.max(1, s.player.maxHp);
    if (ratio > 0 && ratio <= 0.3 && !s.ended) {
      document.body.classList.add('low-hp');
      startHeartbeat();
    } else {
      document.body.classList.remove('low-hp');
      stopHeartbeat();
    }
    return () => { document.body.classList.remove('low-hp'); stopHeartbeat(); };
  }, [combat && combat.state && combat.state.player.hp, combat && combat.state && combat.state.player.maxHp, combat && combat.state && combat.state.ended]);

  const doEmote = () => {
    const pick = EMOTES[Math.floor(Math.random() * EMOTES.length)];
    try { sfx.emote(EMOTE_KIND[pick] || 'slop'); } catch (e) {}
    const id = Math.random();
    setEmote({ id, text: pick });
    setTimeout(() => setEmote(e => (e && e.id === id) ? null : e), 1200);
  };

  if (!combat) return null;
  const s = combat.state;
  const intent = s.enemy.intents[s.enemy.intentIdx];
  const canPlay = s.turnOwner === 'player' && !s.ended;
  const showTelegraph = s.enemy.isBoss || s.enemy.isElite;
  const upcomingIntents = showTelegraph ? computeIntentPreview(s, 2) : [];
  // v1.7 POLISH: end-turn pulse hint when player can't or shouldn't play more
  const noPlayableCards = s.hand.every(cid => {
    const c = CARDS[cid];
    return !c || c.unplayable || (typeof c.cost === 'number' && c.cost > s.player.energy);
  });
  const endTurnHint = canPlay && (s.player.energy === 0 || noPlayableCards);

  // v2.3 QoL #8: warn if there are still playable 0-cost cards in hand when ending the turn.
  const hasFreePlayable = s.hand.some(cid => {
    const c = CARDS[cid];
    return c && !c.unplayable && typeof c.cost === 'number' && c.cost <= s.player.energy && c.cost === 0;
  });
  const handleEndTurnClick = () => {
    if (!canPlay) return;
    if (hasFreePlayable) {
      setConfirmEnd(true);
    } else {
      playEndTurn();
    }
  };
  const confirmEndTurn = () => { setConfirmEnd(false); playEndTurn(); };
  const cancelEndTurn = () => setConfirmEnd(false);

  // v3.7 COMBAT AVATAR: translate a card into a fighter action.
  const triggerFighterFromCard = (card) => {
    if (!card) return;
    const t = String(card.type || '').toLowerCase();
    let action = 'attack';
    if (t === 'power') action = 'power';
    else if (t === 'attack') action = 'attack';
    else action = 'block'; // SKILL / STATUS default → shield up
    setFighterAction(action);
    setFighterTrigger(t => t + 1);
    // Cosmetic block-spark for skill cards
    if (action === 'block') {
      const sid = Math.random();
      setBlockSpark(sid);
      setTimeout(() => setBlockSpark(cur => (cur === sid ? null : cur)), 620);
    }
    setTimeout(() => setFighterAction(cur => (cur === action ? 'idle' : cur)), 500);
  };

  return (
    <div className={`min-h-screen w-full flex flex-col relative overflow-hidden ${screenShakeTier ? `screen-shake-${screenShakeTier}` : ''}`} data-testid="combat-root">
      {/* v1.3: fake brainrot chat ticker (compact, combat corner) */}
      <BrainrotTicker compact testid="combat-brainrot-ticker" />
      {/* v3.9 ENEMY INTRO — cinematic overlay for elite + boss encounters */}
      {enemyIntro && (
        <EnemyIntroCard
          tier={enemyIntro.tier}
          name={enemyIntro.name}
          sprite={enemyIntro.sprite}
          affix={enemyIntro.affix}
        />
      )}
      {/* v3.9 BOSS FINAL BLOW — fullscreen KO cinematic on boss/elite kill */}
      {finalBlow && (
        <div className={`final-blow-overlay is-${finalBlow.tier}`} data-testid="final-blow-overlay" data-tier={finalBlow.tier}>
          <div className="final-blow-flash" />
          <div className="final-blow-shatter">
            <span className="final-blow-shatter-piece p1">{finalBlow.sprite}</span>
            <span className="final-blow-shatter-piece p2">{finalBlow.sprite}</span>
            <span className="final-blow-shatter-piece p3">{finalBlow.sprite}</span>
          </div>
          <div className="final-blow-title" data-testid="final-blow-title">
            {finalBlow.tier === 'boss' ? '☠ SLAIN ☠' : '★ KO ★'}
          </div>
          <div className="final-blow-name" data-testid="final-blow-name">{finalBlow.name}</div>
        </div>
      )}
      {/* v1.2: SLOP METER + tier shout */}
      {comboCount >= 2 && (
        <div className="slop-meter" data-testid="slop-meter" style={{ borderColor: (tierFor(comboCount)?.color || '#39ff14'), color: (tierFor(comboCount)?.color || '#39ff14') }}>
          <Zap size={12} />
          <span data-testid="slop-meter-count">×{comboCount}</span>
          <span className="slop-meter-label">{tierFor(comboCount)?.label || 'COMBO'}</span>
        </div>
      )}
      {comboShout && (
        <div className="slop-shout" style={{ color: comboShout.color, textShadow: `0 0 24px ${comboShout.color}, 4px 4px 0 #000` }} data-testid="slop-shout">
          {comboShout.text}
        </div>
      )}
      <div className="border-b-2 border-[color:var(--neon)] px-6 py-3 flex items-center justify-between bg-[#0a0a0a]">
        <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--text-dim)]">
          FLOOR <span className="text-[color:var(--neon)]">{combat.floorIdx + 1}</span> — {combat.kind.toUpperCase()} FIGHT
          <span className="ml-3 text-[color:var(--muted)]">[1-9] play · [E/SPACE] end</span>
        </div>
        <div className="font-mono text-xs uppercase tracking-widest">
          TURN <span className="text-[color:var(--pink)]" data-testid="combat-turn">{s.turn}</span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-12 gap-6 px-4 md:px-6 py-4 md:py-6 combat-grid">
        <div className={`col-span-3 flex flex-col justify-center gap-4 ${playerShake ? 'shake' : ''}`}>
          <div className="brutal-border p-4 bg-[#0a0a0a] relative cursor-pointer select-none player-portrait"
               onClick={doEmote}
               data-testid="player-portrait"
               title="Click for a brainrot emote">
            <div className="flex items-center gap-3 mb-2">
              <AvatarChip avatar={profile?.avatar} size={44} testid="combat-avatar-chip" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">YOU</div>
                <div className="font-display font-black text-xl text-[color:var(--neon)] truncate">
                  {(playerName || 'GOON').toUpperCase()}
                </div>
                {profile?.avatar?.title && (
                  <div className="text-[9px] uppercase font-mono text-[color:var(--pink)]" data-testid="combat-avatar-title">THE {profile.avatar.title}</div>
                )}
              </div>
            </div>
            <HealthBar hp={s.player.hp} maxHp={s.player.maxHp} block={s.player.block} side="player" testid="player-hp-bar" />
            {emote && (
              <div className="player-emote" data-testid="player-emote">{emote.text}!</div>
            )}
            <div className="mt-3 flex flex-wrap gap-1">
              {s.player.strength > 0 && <BuffBadge label={`STR +${s.player.strength}`} tip={BUFF_TOOLTIPS.STR} />}
              {s.player.weak > 0 && <BuffBadge label={`WEAK ${s.player.weak}`} tip={BUFF_TOOLTIPS.WEAK} debuff />}
              {s.player.vulnerable > 0 && <BuffBadge label={`VULN ${s.player.vulnerable}`} tip={BUFF_TOOLTIPS.VULN} debuff />}
              {s.player.reflect > 0 && <BuffBadge label={`REFLECT ${s.player.reflectAmount}`} tip={BUFF_TOOLTIPS.REFLECT} />}
              {s.player.dodgeNext && <BuffBadge label="DODGE" tip={BUFF_TOOLTIPS.DODGE} />}
              {s.powerDrawExtra > 0 && <BuffBadge label={`DRAW+${s.powerDrawExtra}`} tip={BUFF_TOOLTIPS.DRAW} />}
              {s.powerStrengthGain > 0 && <BuffBadge label={`STR/T +${s.powerStrengthGain}`} tip={BUFF_TOOLTIPS['STR/T']} />}
              {s.powerBlockPerTurn > 0 && <BuffBadge label={`BLK/T +${s.powerBlockPerTurn}`} tip={BUFF_TOOLTIPS.BLK} />}
              {s.powerDoubleWeak && <BuffBadge label="2x WEAK" tip="Weak you apply is doubled." />}
            </div>
            {popups.filter(p => p.target === 'player').map(p => (
              <div key={p.id} className={`dmg-pop ${p.kind === 'heal' ? 'heal-pop' : ''}`}>{p.text}</div>
            ))}
          </div>
        </div>

        <div className={`col-span-6 flex flex-col items-center justify-center gap-6 relative ${enemyShake ? 'shake' : ''} ${enemyShakeHard ? 'shake-hard hit-flash' : ''}`}>
          <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]" data-testid="enemy-name-label">
            {s.enemy.name}
            {s.enemy.affix && (
              <span className="ml-2 brutal-border-red px-1.5 py-0.5 text-[9px] text-[color:var(--red)]" data-testid="enemy-affix">{s.enemy.affix.toUpperCase()}</span>
            )}
            {s.enemy.phases && s.enemy.phases.length > 1 && (
              <span className="ml-2 brutal-border-pink px-1.5 py-0.5 text-[9px] text-[color:var(--pink)]" data-testid="boss-phase-indicator">PHASE {(s.phase || 0) + 1}/{s.enemy.phases.length}</span>
            )}
          </div>
          {combat.state.combatMod && (
            <div className="mt-1 brutal-border-pink px-2 py-1 text-[10px] text-[color:var(--pink)] uppercase tracking-widest" data-testid="combat-modifier">
              ⚠ {combat.state.combatMod.name} · {combat.state.combatMod.desc}
            </div>
          )}
          <div className="combat-stage" data-testid="combat-stage">
            <PlayerFighter
              classId={s.player.classId}
              action={fighterAction}
              trigger={fighterTrigger}
              size={88}
            />
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] combat-stage-vs" data-testid="stage-vs">VS</div>
            <EnemyFighter
              sprite={s.enemy.sprite}
              color={s.enemy.isBoss ? '#ff007f' : '#ff2b2b'}
              action={enemyAction}
              trigger={enemyTrigger}
              isBoss={s.enemy.isBoss}
              size={92}
            >
              {hitSpark && <div className="hit-spark" data-testid="hit-spark" />}
              {blockSpark && <div className="block-spark" data-testid="block-spark">✦</div>}
              {enemyEmote && (
                <div className="enemy-emote" data-testid="enemy-emote">{enemyEmote.text}!</div>
              )}
            </EnemyFighter>
          </div>
          <div className="w-72">
            <HealthBar hp={s.enemy.hp} maxHp={s.enemy.maxHp} block={s.enemy.block} side="enemy" testid="enemy-hp-bar" />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            {s.enemy.strength > 0 && <BuffBadge label={`STR +${s.enemy.strength}`} tip={BUFF_TOOLTIPS.STR} />}
            {s.enemy.weak > 0 && <BuffBadge label={`WEAK ${s.enemy.weak}`} tip={BUFF_TOOLTIPS.WEAK} debuff />}
            {s.enemy.vulnerable > 0 && <BuffBadge label={`VULN ${s.enemy.vulnerable}`} tip={BUFF_TOOLTIPS.VULN} debuff />}
          </div>
          <EnemyIntent intent={intent} enemy={s.enemy} player={s.player} />
          <ComboHUD state={s} />
          {showTelegraph && upcomingIntents.length > 0 && (
            <div className="flex flex-col items-center gap-1" data-testid="intent-telegraph">
              <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] flex items-center gap-1">
                <Eye size={10}/> NEXT TURNS
              </div>
              <div className="flex items-center gap-2">
                {upcomingIntents.map((it, i) => (
                  <div key={i} className="opacity-60 scale-90" data-testid={`intent-preview-${i}`}>
                    <EnemyIntent intent={it} enemy={s.enemy} player={s.player} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {popups.filter(p => p.target === 'enemy').map(p => (
            <div key={p.id} className={`dmg-pop ${p.kind === 'crit' ? 'crit-pop' : ''} ${p.kind === 'phase' ? 'phase-pop' : ''}`}>{p.text}</div>
          ))}
        </div>

        <div className="col-span-3 flex flex-col justify-center items-end gap-4">
          <div className="brutal-border-pink p-4 bg-[#0a0a0a] w-full text-right">
            <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">ENERGY</div>
            <div className="font-display font-black text-5xl text-[color:var(--pink)]" data-testid="player-energy">
              {s.player.energy}<span className="text-[color:var(--text-dim)] text-2xl">/{s.player.maxEnergy}</span>
            </div>
          </div>
          <button
            className={`brutal-btn-pink brutal-btn w-full py-4 text-xl flex items-center justify-center gap-2 ${!canPlay ? 'opacity-50' : ''} ${endTurnHint ? 'end-turn-pulse' : ''}`}
            onClick={handleEndTurnClick}
            disabled={!canPlay}
            data-testid="end-turn-button"
          >
            END TURN <RotateCw size={18} />
          </button>
          {confirmEnd && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="confirm-end-turn-modal">
              <div className="brutal-border bg-[#0a0a0a] max-w-md w-full p-6" style={{ borderColor: 'var(--pink)', boxShadow: '6px 6px 0 var(--pink)' }}>
                <h3 className="font-display font-black text-xl uppercase text-[color:var(--pink)] mb-2">HOLD UP</h3>
                <p className="text-sm text-[color:var(--text-dim)] mb-4">You have 0-cost cards in hand. You can still play them for free! Really end the turn?</p>
                <div className="flex gap-2 justify-end">
                  <button className="brutal-btn text-sm px-4 py-2" onClick={cancelEndTurn} data-testid="confirm-end-cancel">KEEP PLAYING</button>
                  <button className="brutal-btn text-sm px-4 py-2" onClick={confirmEndTurn} data-testid="confirm-end-confirm" style={{ borderColor: 'var(--pink)', color: 'var(--pink)' }}>END ANYWAY</button>
                </div>
              </div>
            </div>
          )}
          <div className="w-full grid grid-cols-3 gap-2 text-xs uppercase font-mono">
            <button
              className="brutal-border-dim p-2 flex items-center justify-between hover:bg-[#0d150d]"
              onClick={() => { sfx.click(); setPilePeek('draw'); }}
              data-testid="pile-peek-draw"
              title="Click / press [ to see draw pile">
              <span className="flex items-center gap-1"><Layers size={12} /> DRAW</span>
              <span className="text-[color:var(--neon)] font-bold" data-testid="draw-pile-count">{s.drawPile.length}</span>
            </button>
            <button
              className="brutal-border-dim p-2 flex items-center justify-between hover:bg-[#150d15]"
              onClick={() => { sfx.click(); setPilePeek('discard'); }}
              data-testid="pile-peek-discard"
              title="Click / press ] to see discard pile">
              <span className="flex items-center gap-1"><Trash2 size={12} /> DISC</span>
              <span className="text-[color:var(--pink)] font-bold" data-testid="discard-pile-count">{s.discardPile.length}</span>
            </button>
            <button
              className="brutal-border-dim p-2 flex items-center justify-between hover:bg-[#150d05]"
              onClick={() => { sfx.click(); setPilePeek('exhaust'); }}
              data-testid="pile-peek-exhaust"
              title="Cards exhausted this combat">
              <span className="flex items-center gap-1"><Flame size={12} /> EXH</span>
              <span className="text-[color:var(--red)] font-bold" data-testid="exhaust-pile-count">{(s.exhaustPile || []).length}</span>
            </button>
          </div>
          {/* v1.7: log + pause + v1.8 undo quick-access */}
          <div className="w-full flex gap-2 mt-1 text-[10px] uppercase font-mono">
            <button
              className={`brutal-border-dim px-2 py-1 flex-1 flex items-center justify-center gap-1 ${canUndo ? 'hover:bg-[#1a1010]' : 'opacity-40 cursor-not-allowed'}`}
              onClick={() => { if (canUndo) { sfx.click(); undoLastCard(); } }}
              data-testid="undo-card-button"
              disabled={!canUndo}
              title="Press U to undo the last card played this turn">
              ↶ UNDO
            </button>
            <button
              className="brutal-border-dim px-2 py-1 flex-1 flex items-center justify-center gap-1 hover:bg-[#0d0d0d]"
              onClick={() => { sfx.click(); setShowLog(v => !v); }}
              data-testid="battle-log-toggle"
              title="Press L to toggle log">
              <ScrollText size={11}/> LOG
            </button>
            <button
              className="brutal-border-dim px-2 py-1 flex-1 flex items-center justify-center gap-1 hover:bg-[#0d0d0d]"
              onClick={() => { sfx.click(); setPaused(true); }}
              data-testid="pause-button"
              title="Press ESC to pause">
              <Pause size={11}/> PAUSE
            </button>
          </div>
        </div>
      </div>

      <div className="border-t-2 border-[color:var(--neon)] bg-[#050505] px-4 md:px-6 py-4 md:py-6 min-h-[220px] md:min-h-[280px] flex items-center justify-center relative combat-hand-scroll">
        {/* v1.4: keyboard hint bar */}
        <div className="hidden md:flex absolute top-1 left-1/2 -translate-x-1/2 items-center gap-3 text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] font-mono pointer-events-none" data-testid="kb-hint-bar">
          <span><kbd className="kb-key">1</kbd>–<kbd className="kb-key">9</kbd> play</span>
          <span><kbd className="kb-key">E</kbd> end</span>
          <span><kbd className="kb-key">U</kbd> undo</span>
          <span><kbd className="kb-key">L</kbd> log</span>
          <span><kbd className="kb-key">ESC</kbd> pause</span>
          <span><kbd className="kb-key">RCLK</kbd> zoom card</span>
        </div>
        <div className="flex items-end gap-3 pt-4" data-testid="player-hand">
          <AnimatePresence initial={false}>
          {s.hand.map((cardId, i) => {
            const c = CARDS[cardId];
            const disabled = !canPlay || s.player.energy < c.cost;
            const projLabel = projectionLabel(c, s); // v2.4 hover tooltip
            return (
              <motion.div
                key={`${cardId}-${i}-${s.turn}`}
                layout
                initial={{ y: 80, opacity: 0, rotate: -6 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: -60, opacity: 0, rotate: 6, scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 22, delay: i * 0.05 }}
                className="relative group"
                onContextMenu={(e) => { e.preventDefault(); setZoomCard(cardId); }}
              >
                <GameCard
                  cardId={cardId}
                  onClick={() => {
                    // v1.2: per-card-type sfx flavor on top of base cardPlay
                    try {
                      const c2 = CARDS[cardId];
                      const t = String(c2?.type || '').toLowerCase();
                      if (t === 'attack') sfx.attackCard();
                      else if (t === 'power') sfx.powerCard();
                      else sfx.skillCard();
                      triggerFighterFromCard(c2);
                    } catch (e) {}
                    doPlayCard(i);
                  }}
                  disabled={disabled}
                  testid={`hand-card-${i}`}
                />
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-dim)]">[{i+1}]</div>
                {/* v2.4 CARD PROJECTION TOOLTIP */}
                {projLabel && (
                  <div
                    className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 whitespace-nowrap brutal-border-dim bg-[#0a0a0a]"
                    style={{ borderColor: 'var(--neon)', color: 'var(--neon)', boxShadow: '2px 2px 0 var(--neon)' }}
                    data-testid={`hand-card-projection-${i}`}
                  >
                    {projLabel}
                  </div>
                )}
              </motion.div>
            );
          })}
          </AnimatePresence>
          {s.hand.length === 0 && (
            <div className="text-[color:var(--text-dim)] uppercase tracking-widest text-sm">— empty hand —</div>
          )}
        </div>
      </div>

      {/* v1.8 DEPTH: Card zoom on right-click */}
      {zoomCard && (
        <div className="card-zoom-overlay" onClick={() => setZoomCard(null)} data-testid="card-zoom-overlay">
          <div className="card-zoom-inner" onClick={(e) => e.stopPropagation()}>
            <div className="card-zoom-scale">
              <GameCard cardId={zoomCard} disabled testid="card-zoom-card" />
            </div>
            <div className="text-center mt-4 text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-dim)]">right-click any card · click anywhere to close</div>
          </div>
        </div>
      )}

      {/* v1.7 POLISH: Pile Peek Modal */}
      {pilePeek && (
        <PilePeekModal
          kind={pilePeek}
          cards={pilePeek === 'draw' ? s.drawPile : pilePeek === 'discard' ? s.discardPile : (s.exhaustPile || [])}
          onClose={() => setPilePeek(null)}
        />
      )}

      {/* v1.7 POLISH: Pause Overlay */}
      {paused && (
        <div className="pause-overlay" data-testid="pause-overlay" onClick={() => setPaused(false)}>
          <div className="pause-panel" onClick={e => e.stopPropagation()}>
            <div className="text-[color:var(--pink)] font-mono uppercase tracking-widest text-xs">PAUSED</div>
            <div className="font-display font-black text-5xl text-[color:var(--neon)] mt-2 mb-6">TAKE A BREATH</div>
            <button className="brutal-btn text-lg w-full mb-3" data-testid="pause-resume" onClick={() => setPaused(false)}>
              <Pause size={16}/> RESUME
            </button>
            <button className="brutal-border-dim p-3 w-full mb-3 hover:bg-[#0d0d0d] uppercase font-mono text-xs flex items-center justify-center gap-2"
                    data-testid="pause-log-btn"
                    onClick={() => { setShowLog(true); setPaused(false); }}>
              <ScrollText size={14}/> VIEW BATTLE LOG
            </button>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] mt-4 font-mono">
              Shortcuts: <kbd className="kb-key">ESC</kbd> resume · <kbd className="kb-key">L</kbd> log · <kbd className="kb-key">[</kbd>/<kbd className="kb-key">]</kbd> piles
            </div>
          </div>
        </div>
      )}

      {/* v1.7 POLISH: Battle Log Panel */}
      {showLog && (
        <div className="battle-log-panel" data-testid="battle-log-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="font-display font-black text-lg text-[color:var(--neon)] flex items-center gap-2">
              <ScrollText size={16}/> BATTLE LOG
            </div>
            <button className="text-[color:var(--text-dim)] hover:text-white" onClick={() => setShowLog(false)} data-testid="battle-log-close">
              <X size={18}/>
            </button>
          </div>
          <div className="battle-log-lines" data-testid="battle-log-lines">
            {(s.log && s.log.length > 0)
              ? s.log.slice().reverse().map((line, i) => (
                  <div key={i} className="battle-log-line">{line}</div>
                ))
              : <div className="text-[color:var(--text-dim)] italic">No entries yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// v1.7 POLISH: Pile peek modal shows the actual cards in a pile (unshuffled reveal is a small QoL info leak but standard for the genre)
function PilePeekModal({ kind, cards, onClose }) {
  const label = kind.toUpperCase();
  return (
    <div className="pile-peek-overlay" onClick={onClose} data-testid={`pile-peek-${kind}`}>
      <div className="pile-peek-panel" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-black text-2xl text-[color:var(--neon)] uppercase">
            {label} PILE — <span className="text-[color:var(--text-dim)]">{cards.length}</span>
          </div>
          <button onClick={onClose} className="brutal-btn text-xs" data-testid="pile-peek-close">
            <X size={14}/> CLOSE
          </button>
        </div>
        {cards.length === 0 ? (
          <div className="py-16 text-center text-[color:var(--text-dim)] uppercase tracking-widest">— EMPTY —</div>
        ) : (
          <div className="flex flex-wrap gap-3 justify-center">
            {cards.map((cardId, i) => (
              <GameCard key={i} cardId={cardId} disabled testid={`pile-peek-card-${kind}-${i}`} />
            ))}
          </div>
        )}
        <div className="mt-4 text-center text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] font-mono">
          {kind === 'draw' && '⚠ Draw pile order is shuffled — this is just an inventory.'}
          {kind === 'discard' && 'Cards you played this combat.'}
          {kind === 'exhaust' && 'Cards removed until end of combat.'}
        </div>
      </div>
    </div>
  );
}

function BuffBadge({ label, tip, debuff }) {
  return (
    <span className={`buff-badge ${debuff ? 'debuff' : ''} buff-badge-hoverable`} data-tip={tip} data-testid={`buff-${label.split(' ')[0].toLowerCase()}`}>
      {label}
      {tip && <span className="buff-tooltip">{tip}</span>}
    </span>
  );
}
